import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  Stack,
  IconButton,
  Tooltip,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  Save as SaveIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { TradingRules, TradingRulesVersion, Portfolio } from '../types';
import { TradingRulesService } from '../services/tradingRulesService';
import { PortfolioService } from '../services/portfolioService';
import { defaultGlobalRules, conservativeRules, aggressiveRules } from '../config/defaultTradingRules';
import { useUser } from '../contexts/UserContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const TradingRulesPage: React.FC = () => {
  const { profile } = useUser();
  const [searchParams] = useSearchParams();
  const portfolioParam = searchParams.get('portfolio');
  const [tabValue, setTabValue] = useState(portfolioParam ? 1 : 0);
  const [globalRules, setGlobalRules] = useState<TradingRules | null>(null);
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [portfolioRules, setPortfolioRules] = useState<{ [key: string]: TradingRules }>({});
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Draft workflow state
  const [drafts, setDrafts] = useState<{ [rulesId: string]: TradingRulesVersion }>({});
  const [editingDraftId, setEditingDraftId] = useState<string | null>(null); // rulesId being edited
  const [draftRules, setDraftRules] = useState<Partial<TradingRules> | null>(null);

  // Activation dialog
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [activateRulesId, setActivateRulesId] = useState<string | null>(null);
  const [activateDraftId, setActivateDraftId] = useState<string | null>(null);
  const [changeNotes, setChangeNotes] = useState('');

  // Version history
  const [historyOpen, setHistoryOpen] = useState<{ [rulesId: string]: boolean }>({});
  const [versionHistory, setVersionHistory] = useState<{ [rulesId: string]: TradingRulesVersion[] }>({});
  const [viewingVersion, setViewingVersion] = useState<TradingRulesVersion | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load global rules
      const global = await TradingRulesService.getGlobalRules();
      if (!global) {
        await TradingRulesService.initializeDefaultRules();
        const newGlobal = await TradingRulesService.getGlobalRules();
        setGlobalRules(newGlobal);
        // Check for draft
        if (newGlobal) await loadDraft(newGlobal.id);
      } else {
        setGlobalRules(global);
        await loadDraft(global.id);
      }

      // Load portfolios
      const allPortfolios = await PortfolioService.getAllPortfolios();
      setPortfolios(allPortfolios.filter(p => p.isActive));

      // Load portfolio-specific rules and their drafts
      const rules: { [key: string]: TradingRules } = {};
      for (const portfolio of allPortfolios) {
        const pRules = await TradingRulesService.getPortfolioRules(portfolio.id);
        if (!pRules.isGlobal) {
          rules[portfolio.id] = pRules;
          await loadDraft(pRules.id);
        }
      }
      setPortfolioRules(rules);

      if (portfolioParam && allPortfolios.some(p => p.id === portfolioParam)) {
        setSelectedPortfolioId(portfolioParam);
      } else if (allPortfolios.length > 0) {
        setSelectedPortfolioId(allPortfolios[0].id);
      }
    } catch (error) {
      console.error('Error loading rules:', error);
      setError('Failed to load trading rules');
    } finally {
      setLoading(false);
    }
  };

  const loadDraft = async (rulesId: string) => {
    try {
      const draft = await TradingRulesService.getDraft(rulesId);
      if (draft) {
        setDrafts(prev => ({ ...prev, [rulesId]: draft }));
      }
    } catch (error) {
      console.error('Error loading draft for', rulesId, error);
    }
  };

  // ========== Draft Workflow Handlers ==========

  const handleStartEditing = async (rules: TradingRules) => {
    if (rules.isGlobal && !profile?.isAdmin) {
      setError('Only administrators can edit global trading rules.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      let draft = drafts[rules.id];
      if (!draft) {
        draft = await TradingRulesService.createDraft(rules.id);
        setDrafts(prev => ({ ...prev, [rules.id]: draft }));
      }
      setEditingDraftId(rules.id);
      setDraftRules(draft.rules as Partial<TradingRules>);
    } catch (error) {
      console.error('Error creating draft:', error);
      setError('Failed to create draft');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!editingDraftId || !draftRules) return;
    const draft = drafts[editingDraftId];
    if (!draft) return;

    setSaving(true);
    setError(null);
    try {
      await TradingRulesService.updateDraft(editingDraftId, draft.id, draftRules as Partial<TradingRules>);
      // Update local draft state
      setDrafts(prev => ({
        ...prev,
        [editingDraftId]: { ...draft, rules: draftRules }
      }));
      setEditingDraftId(null);
      setDraftRules(null);
    } catch (error) {
      console.error('Error saving draft:', error);
      setError('Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEditing = () => {
    setEditingDraftId(null);
    setDraftRules(null);
    setError(null);
  };

  const handleOpenActivateDialog = (rulesId: string, draftId: string) => {
    setActivateRulesId(rulesId);
    setActivateDraftId(draftId);
    setChangeNotes('');
    setActivateDialogOpen(true);
  };

  const handleActivate = async () => {
    if (!activateRulesId || !activateDraftId) return;

    setSaving(true);
    setError(null);
    try {
      await TradingRulesService.activateDraft(activateRulesId, activateDraftId, changeNotes || undefined);

      // Refresh data
      setEditingDraftId(null);
      setDraftRules(null);
      setActivateDialogOpen(false);
      setDrafts(prev => {
        const next = { ...prev };
        delete next[activateRulesId!];
        return next;
      });

      // Reload the rules
      await loadData();
    } catch (error) {
      console.error('Error activating draft:', error);
      setError('Failed to activate draft');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscardDraft = async (rulesId: string) => {
    const draft = drafts[rulesId];
    if (!draft) return;

    setSaving(true);
    setError(null);
    try {
      await TradingRulesService.discardDraft(rulesId, draft.id);
      setDrafts(prev => {
        const next = { ...prev };
        delete next[rulesId];
        return next;
      });
      setEditingDraftId(null);
      setDraftRules(null);
    } catch (error) {
      console.error('Error discarding draft:', error);
      setError('Failed to discard draft');
    } finally {
      setSaving(false);
    }
  };

  // ========== Version History ==========

  const handleToggleHistory = async (rulesId: string) => {
    const isOpen = historyOpen[rulesId];
    setHistoryOpen(prev => ({ ...prev, [rulesId]: !isOpen }));

    if (!isOpen && !versionHistory[rulesId]) {
      try {
        const history = await TradingRulesService.getVersionHistory(rulesId);
        setVersionHistory(prev => ({ ...prev, [rulesId]: history }));
      } catch (error) {
        console.error('Error loading version history:', error);
      }
    }
  };

  // ========== Template handling ==========

  const handleApplyTemplate = (template: 'conservative' | 'moderate' | 'aggressive') => {
    if (!draftRules) return;

    const templateRules = template === 'conservative' ? conservativeRules
      : template === 'aggressive' ? aggressiveRules
      : defaultGlobalRules;

    setDraftRules({
      ...draftRules,
      ...templateRules,
    });
  };

  const handleCreatePortfolioRules = async (portfolioId: string) => {
    setSaving(true);
    setError(null);

    try {
      const portfolio = portfolios.find(p => p.id === portfolioId);
      if (!portfolio) return;

      const newRules = await TradingRulesService.createPortfolioRules(
        portfolioId,
        { name: `${portfolio.name} Rules` }
      );

      setPortfolioRules({
        ...portfolioRules,
        [portfolioId]: newRules
      });
    } catch (error) {
      console.error('Error creating portfolio rules:', error);
      setError('Failed to create portfolio rules');
    } finally {
      setSaving(false);
    }
  };

  // ========== Rendering ==========

  const renderVersionChip = (rules: TradingRules) => {
    const version = rules.version ?? 1;
    const draft = drafts[rules.id];

    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip
          label={`Active v${version}`}
          size="small"
          sx={{
            bgcolor: 'success.main',
            color: 'white',
            fontWeight: 'bold',
          }}
        />
        {draft && (
          <Chip
            label={`Draft v${draft.version}`}
            size="small"
            sx={{
              bgcolor: 'warning.main',
              color: 'white',
              fontWeight: 'bold',
            }}
          />
        )}
      </Stack>
    );
  };

  const renderRulesFields = (
    currentRules: Partial<TradingRules>,
    isEditing: boolean,
    onChange: (updates: Partial<TradingRules>) => void
  ) => (
    <Grid container spacing={3}>
      {/* Position Management */}
      <Grid item xs={12}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Position Management
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>

      <Grid item xs={12} sm={4}>
        <TextField
          label="Max Holding Days"
          type="number"
          value={currentRules.maxHoldingDays ?? ''}
          onChange={(e) => onChange({ maxHoldingDays: parseInt(e.target.value) || 0 })}
          disabled={!isEditing}
          fullWidth
          InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
          helperText="Maximum time to hold a position"
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        <TextField
          label="Position Cooldown"
          type="number"
          value={currentRules.positionCooldownDays ?? ''}
          onChange={(e) => onChange({ positionCooldownDays: parseInt(e.target.value) || 0 })}
          disabled={!isEditing}
          fullWidth
          InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
          helperText="Wait period before re-entering"
        />
      </Grid>

      {/* Entry Rules */}
      <Grid item xs={12}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
          Entry Rules
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>

      <Grid item xs={12} sm={4}>
        <TextField
          label="Max Distance from Pivot"
          type="number"
          value={currentRules.maxDistanceFromPivot ?? ''}
          onChange={(e) => onChange({ maxDistanceFromPivot: parseFloat(e.target.value) || 0 })}
          disabled={!isEditing}
          fullWidth
          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          helperText="Maximum % from pivot to enter"
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
          <TextField
            label="Min Volume Increase"
            type="number"
            value={currentRules.minVolumeIncrease ?? ''}
            onChange={(e) => onChange({ minVolumeIncrease: parseFloat(e.target.value) || 0 })}
            disabled={!isEditing}
            fullWidth
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            helperText="Min volume vs 50-day average"
          />
          <Tooltip title="Minimum volume compared to 50-day average. Positive values (e.g., 10) require volume above average. Negative values (e.g., -60) allow stocks with partial volume - a value of -60 means the stock only needs 40% of its average volume to qualify.">
            <IconButton size="small" sx={{ mt: 1 }}>
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Grid>

      <Grid item xs={12} sm={4}>
        <TextField
          label="Min Entry Score"
          type="number"
          value={currentRules.minEntryScore ?? 60}
          onChange={(e) => onChange({ minEntryScore: parseInt(e.target.value) || 0 })}
          disabled={!isEditing}
          fullWidth
          InputProps={{ endAdornment: <InputAdornment position="end">pts</InputAdornment> }}
          helperText="Minimum score for entry (0-100)"
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
          <TextField
            label="Min Day Price Change"
            type="number"
            value={currentRules.minDayPriceChange ?? 0}
            onChange={(e) => onChange({ minDayPriceChange: parseFloat(e.target.value) || 0 })}
            disabled={!isEditing}
            fullWidth
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            helperText="Min daily price change (0 = disabled)"
          />
          <Tooltip title="Minimum daily price change percentage required for entry. Set to 0 to disable this filter. Positive values require the stock to be up at least that amount.">
            <IconButton size="small" sx={{ mt: 1 }}>
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Grid>

      <Grid item xs={12} sm={4}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
          <TextField
            label="Min Tracked Price Change"
            type="number"
            value={currentRules.minTrackedPriceChange ?? 0}
            onChange={(e) => onChange({ minTrackedPriceChange: parseFloat(e.target.value) || 0 })}
            disabled={!isEditing}
            fullWidth
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            helperText="Min change since tracking (0 = disabled)"
          />
          <Tooltip title="Minimum price change since the stock was added to your watchlist. Set to 0 to disable this filter.">
            <IconButton size="small" sx={{ mt: 1 }}>
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Grid>

      <Grid item xs={12} sm={4}>
        <FormControlLabel
          control={
            <Switch
              checked={currentRules.requirePositiveTrend ?? false}
              onChange={(e) => onChange({ requirePositiveTrend: e.target.checked })}
              disabled={!isEditing}
            />
          }
          label="Require Positive Trend"
        />
      </Grid>

      {/* Exit Rules */}
      <Grid item xs={12}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
          Exit Rules
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>

      <Grid item xs={12} sm={4}>
        <TextField
          label="Stop Loss"
          type="number"
          value={currentRules.stopLossPercentage ?? ''}
          onChange={(e) => onChange({ stopLossPercentage: parseFloat(e.target.value) || 0 })}
          disabled={!isEditing}
          fullWidth
          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          helperText="Stop loss from entry price"
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        <TextField
          label="Target Gain"
          type="number"
          value={currentRules.targetGainPercentage ?? ''}
          onChange={(e) => onChange({ targetGainPercentage: parseFloat(e.target.value) || 0 })}
          disabled={!isEditing}
          fullWidth
          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          helperText="Profit target % above pivot"
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        <TextField
          label="Min Target from Entry"
          type="number"
          value={currentRules.minTargetGainFromEntry ?? 5}
          onChange={(e) => onChange({ minTargetGainFromEntry: parseFloat(e.target.value) || 0 })}
          disabled={!isEditing}
          fullWidth
          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          helperText="Min target % above entry price"
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        <TextField
          label="Max Target from Pivot"
          type="number"
          value={currentRules.maxTargetGainFromPivot ?? 25}
          onChange={(e) => onChange({ maxTargetGainFromPivot: parseFloat(e.target.value) || 0 })}
          disabled={!isEditing}
          fullWidth
          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          helperText="Max target % above pivot (cap)"
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        <TextField
          label="Trailing Stop Activation"
          type="number"
          value={currentRules.trailingStopActivation ?? 0}
          onChange={(e) => onChange({ trailingStopActivation: parseFloat(e.target.value) || 0 })}
          disabled={!isEditing}
          fullWidth
          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          helperText="Gain % to activate trailing stop"
        />
      </Grid>

      {/* Risk Management */}
      <Grid item xs={12}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
          Risk Management
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          label="Max Sector Concentration"
          type="number"
          value={currentRules.maxSectorConcentration ?? ''}
          onChange={(e) => onChange({ maxSectorConcentration: parseFloat(e.target.value) || 0 })}
          disabled={!isEditing}
          fullWidth
          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          helperText="Maximum % in one sector"
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          label="Days Between Same Stock"
          type="number"
          value={currentRules.minDaysBetweenSameStock ?? ''}
          onChange={(e) => onChange({ minDaysBetweenSameStock: parseInt(e.target.value) || 0 })}
          disabled={!isEditing}
          fullWidth
          InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
          helperText="Cooldown for re-entering same stock"
        />
      </Grid>

      {/* Adjustment Rules */}
      <Grid item xs={12}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
          Adjustment Rules
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>

      <Grid item xs={12} sm={3}>
        <TextField
          label="Target Adjust Trigger"
          type="number"
          value={currentRules.targetAdjustmentTrigger ?? 10}
          onChange={(e) => onChange({ targetAdjustmentTrigger: parseFloat(e.target.value) || 0 })}
          disabled={!isEditing}
          fullWidth
          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          helperText="Gain % to trigger target review"
        />
      </Grid>

      <Grid item xs={12} sm={3}>
        <TextField
          label="Target Distance Threshold"
          type="number"
          value={currentRules.targetAdjustmentThreshold ?? 10}
          onChange={(e) => onChange({ targetAdjustmentThreshold: parseFloat(e.target.value) || 0 })}
          disabled={!isEditing}
          fullWidth
          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          helperText="Min distance to suggest adjustment"
        />
      </Grid>

      <Grid item xs={12} sm={3}>
        <TextField
          label="New Target Buffer"
          type="number"
          value={currentRules.targetAdjustmentBuffer ?? 5}
          onChange={(e) => onChange({ targetAdjustmentBuffer: parseFloat(e.target.value) || 0 })}
          disabled={!isEditing}
          fullWidth
          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          helperText="% above current for new target"
        />
      </Grid>

      <Grid item xs={12} sm={3}>
        <TextField
          label="Trailing Stop Buffer"
          type="number"
          value={currentRules.trailingStopBuffer ?? 1}
          onChange={(e) => onChange({ trailingStopBuffer: parseFloat(e.target.value) || 0 })}
          disabled={!isEditing}
          fullWidth
          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          helperText="% above entry for stop"
        />
      </Grid>
    </Grid>
  );

  const renderRulesCard = (rules: TradingRules) => {
    const draft = drafts[rules.id];
    const isEditingThis = editingDraftId === rules.id;
    const hasDraft = !!draft;
    const canEdit = !rules.isGlobal || profile?.isAdmin;
    const isHistoryOpen = historyOpen[rules.id] ?? false;

    // Determine what to display: editing draft, viewing draft, or active rules
    let displayRules: Partial<TradingRules>;
    let isEditing = false;

    if (isEditingThis && draftRules) {
      displayRules = draftRules;
      isEditing = true;
    } else if (viewingVersion && viewingVersion.rulesId === rules.id) {
      displayRules = viewingVersion.rules;
      isEditing = false;
    } else if (hasDraft && !isEditingThis) {
      // Show active rules with draft banner
      displayRules = rules;
      isEditing = false;
    } else {
      displayRules = rules;
      isEditing = false;
    }

    return (
      <Card
        sx={{
          borderLeft: 4,
          borderLeftColor: hasDraft ? 'warning.main' : 'success.main',
        }}
      >
        <CardContent>
          {/* Header with title and version chip */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6">{rules.name}</Typography>
              {renderVersionChip(rules)}
            </Box>
            <Box>
              {isEditingThis ? (
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleCancelEditing}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveDraft}
                    disabled={saving}
                  >
                    Save Draft
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={<CheckIcon />}
                    onClick={() => {
                      // Save draft first, then open activate dialog
                      handleSaveDraft().then(() => {
                        const d = drafts[rules.id];
                        if (d) handleOpenActivateDialog(rules.id, d.id);
                      });
                    }}
                    disabled={saving}
                  >
                    Activate
                  </Button>
                </Stack>
              ) : viewingVersion && viewingVersion.rulesId === rules.id ? (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setViewingVersion(null)}
                >
                  Back to Active
                </Button>
              ) : (
                <Stack direction="row" spacing={1}>
                  {hasDraft && canEdit && (
                    <>
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        startIcon={<EditIcon />}
                        onClick={() => {
                          setEditingDraftId(rules.id);
                          setDraftRules(draft!.rules as Partial<TradingRules>);
                        }}
                      >
                        Edit Draft
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<CheckIcon />}
                        onClick={() => handleOpenActivateDialog(rules.id, draft!.id)}
                        disabled={saving}
                      >
                        Activate v{draft!.version}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDiscardDraft(rules.id)}
                        disabled={saving}
                      >
                        Discard
                      </Button>
                    </>
                  )}
                  {!hasDraft && canEdit && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => handleStartEditing(rules)}
                      disabled={saving}
                    >
                      Edit
                    </Button>
                  )}
                  {!canEdit && rules.isGlobal && (
                    <Tooltip title="Only administrators can edit global trading rules.">
                      <span>
                        <Button size="small" variant="outlined" disabled startIcon={<EditIcon />}>
                          Edit
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </Stack>
              )}
            </Box>
          </Box>

          {/* Draft pending banner */}
          {hasDraft && !isEditingThis && !viewingVersion && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              You have draft changes (v{draft!.version}) pending activation.
            </Alert>
          )}

          {/* Viewing archived version banner */}
          {viewingVersion && viewingVersion.rulesId === rules.id && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Viewing archived version v{viewingVersion.version}
              {viewingVersion.changeNotes && ` — "${viewingVersion.changeNotes}"`}
              {' (read-only)'}
            </Alert>
          )}

          {/* Template buttons in edit mode */}
          {isEditingThis && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                Apply Template:
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="outlined" onClick={() => handleApplyTemplate('conservative')}>
                  Conservative
                </Button>
                <Button size="small" variant="outlined" onClick={() => handleApplyTemplate('moderate')}>
                  Moderate
                </Button>
                <Button size="small" variant="outlined" onClick={() => handleApplyTemplate('aggressive')}>
                  Aggressive
                </Button>
              </Stack>
            </Box>
          )}

          {rules.description && (
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              {rules.description}
            </Typography>
          )}

          {/* Rules Fields */}
          {renderRulesFields(
            displayRules,
            isEditing,
            (updates) => {
              if (draftRules) {
                setDraftRules({ ...draftRules, ...updates });
              }
            }
          )}

          {/* Version History Toggle */}
          <Box sx={{ mt: 3 }}>
            <Button
              size="small"
              startIcon={<HistoryIcon />}
              endIcon={isHistoryOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => handleToggleHistory(rules.id)}
            >
              Version History
            </Button>
            <Collapse in={isHistoryOpen}>
              <List dense sx={{ mt: 1 }}>
                {(versionHistory[rules.id] || []).map((v) => (
                  <ListItem
                    key={v.id}
                    sx={{
                      bgcolor: v.status === 'active' ? 'action.selected' : 'transparent',
                      borderRadius: 1,
                      mb: 0.5,
                      cursor: v.status === 'archived' ? 'pointer' : 'default',
                    }}
                    onClick={() => {
                      if (v.status === 'archived') {
                        setViewingVersion(v);
                      }
                    }}
                  >
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" fontWeight="bold">
                            v{v.version}
                          </Typography>
                          <Chip
                            label={v.status}
                            size="small"
                            color={
                              v.status === 'active' ? 'success' :
                              v.status === 'draft' ? 'warning' : 'default'
                            }
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Stack>
                      }
                      secondary={
                        <>
                          {v.activatedAt
                            ? new Date(v.activatedAt).toLocaleDateString('en-US', {
                                month: 'short', day: 'numeric', year: 'numeric',
                                hour: 'numeric', minute: '2-digit',
                              })
                            : v.createdAt
                            ? new Date(v.createdAt).toLocaleDateString('en-US', {
                                month: 'short', day: 'numeric', year: 'numeric',
                                hour: 'numeric', minute: '2-digit',
                              })
                            : ''
                          }
                          {v.changeNotes && ` — ${v.changeNotes}`}
                        </>
                      }
                    />
                  </ListItem>
                ))}
                {(versionHistory[rules.id] || []).length === 0 && (
                  <ListItem>
                    <ListItemText secondary="No version history yet" />
                  </ListItem>
                )}
              </List>
            </Collapse>
          </Box>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Trading Rules Configuration
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label="Global Rules" />
          <Tab label="Portfolio Rules" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        {!profile?.isAdmin && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Global trading rules apply to all portfolios by default. As a non-admin user, you can view these rules
              but cannot modify them. You can create custom rules for your individual portfolios in the Portfolio Rules tab.
            </Typography>
          </Alert>
        )}
        {globalRules && renderRulesCard(globalRules)}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Select Portfolio</InputLabel>
          <Select
            value={selectedPortfolioId}
            onChange={(e) => setSelectedPortfolioId(e.target.value as string)}
            label="Select Portfolio"
          >
            {portfolios.map(portfolio => (
              <MenuItem key={portfolio.id} value={portfolio.id}>
                {portfolio.name}
                {portfolioRules[portfolio.id] && (
                  <Chip
                    label="Custom Rules"
                    size="small"
                    sx={{ ml: 1 }}
                    color="primary"
                  />
                )}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedPortfolioId && (
          <>
            {portfolioRules[selectedPortfolioId] ? (
              renderRulesCard(portfolioRules[selectedPortfolioId])
            ) : (
              <Card>
                <CardContent>
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6" gutterBottom>
                      Using Global Rules
                    </Typography>
                    <Typography variant="body2" color="textSecondary" paragraph>
                      This portfolio is currently using the global trading rules.
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<CopyIcon />}
                      onClick={() => handleCreatePortfolioRules(selectedPortfolioId)}
                      disabled={saving}
                    >
                      Create Custom Rules
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </TabPanel>

      {/* Activation Dialog */}
      <Dialog open={activateDialogOpen} onClose={() => setActivateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Activate Draft Rules</DialogTitle>
        <DialogContent>
          {activateRulesId && globalRules?.id === activateRulesId && globalRules?.isGlobal && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This updates the default rules for all users who don't have custom portfolio rules.
            </Alert>
          )}
          <Typography variant="body2" sx={{ mb: 2 }}>
            Activating will make the draft rules live. Optionally add a note describing what changed.
          </Typography>
          <TextField
            label="Change Notes (optional)"
            placeholder="e.g., Lowered stop loss to 5%"
            value={changeNotes}
            onChange={(e) => setChangeNotes(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleActivate}
            disabled={saving}
            startIcon={<CheckIcon />}
          >
            Activate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TradingRulesPage;
